import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Overlay, Popover } from 'react-bootstrap';
import CalendarChip from './CalendarChip.jsx';
import {
	CALENDAR_OVERFLOW_CLOSE_MS,
	CALENDAR_OVERFLOW_OPEN_MS,
	CALENDAR_OVERFLOW_POINTER_PAD,
	calendarOverflowShouldClose,
	calendarOverflowShouldOpen,
	pointerHitsRect
} from '../util/calendarOverflowHover.js';

const POPPER_CONFIG = {
	strategy: 'fixed',
	modifiers: [
		{
			name: 'preventOverflow',
			options: { boundary: 'viewport', padding: 8, altAxis: true, tether: true }
		},
		{
			name: 'flip',
			options: {
				fallbackPlacements: ['top-start', 'bottom-end', 'top-end', 'right-start', 'left-start'],
				padding: 8
			}
		}
	]
};

const CalendarOverflow = ({
	dayKey,
	activities,
	datasetId,
	colorBy,
	codeTypeId,
	datasetInfo
}) => {
	const triggerRef = useRef(null);
	const popoverRef = useRef(null);
	const openTimer = useRef(null);
	const closeTimer = useRef(null);
	const openRef = useRef(false);
	const pinnedRef = useRef(false);
	const focusListRef = useRef(false);
	const suppressFocusOpenRef = useRef(false);
	const pointerRef = useRef({ x: 0, y: 0, valid: false });
	const [open, setOpen] = useState(false);
	const count = activities.length;

	const setOpenSafe = value => {
		openRef.current = value;
		setOpen(value);
		if (!value) {
			pinnedRef.current = false;
			focusListRef.current = false;
		}
	};

	const clearTimers = () => {
		if (openTimer.current) {
			clearTimeout(openTimer.current);
			openTimer.current = null;
		}
		if (closeTimer.current) {
			clearTimeout(closeTimer.current);
			closeTimer.current = null;
		}
	};

	const containsTarget = node => {
		if (!node) {
			return false;
		}
		if (triggerRef.current && triggerRef.current.contains(node)) {
			return true;
		}
		if (popoverRef.current && popoverRef.current.contains(node)) {
			return true;
		}
		return false;
	};

	const closeNow = () => {
		clearTimers();
		setOpenSafe(false);
	};

	const closeAndFocusTrigger = () => {
		suppressFocusOpenRef.current = true;
		closeNow();
		if (triggerRef.current) {
			triggerRef.current.focus();
		}
		window.setTimeout(() => {
			suppressFocusOpenRef.current = false;
		}, 0);
	};

	const notePointer = event => {
		if (!event || typeof event.clientX !== 'number') {
			return;
		}
		pointerRef.current = { x: event.clientX, y: event.clientY, valid: true };
	};

	const isPointerInside = (pad = 0) => {
		const pointer = pointerRef.current;
		if (!pointer.valid) {
			return false;
		}
		if (pad === 0 && typeof document !== 'undefined' && document.elementFromPoint) {
			const node = document.elementFromPoint(pointer.x, pointer.y);
			if (containsTarget(node)) {
				return true;
			}
		}
		const rects = [triggerRef.current, popoverRef.current]
			.filter(Boolean)
			.map(node => node.getBoundingClientRect());
		return rects.some(rect => pointerHitsRect(pointer.x, pointer.y, rect, pad));
	};

	const isFocusInside = () => containsTarget(document.activeElement);

	const scheduleOpen = () => {
		if (suppressFocusOpenRef.current) {
			return;
		}
		if (closeTimer.current) {
			clearTimeout(closeTimer.current);
			closeTimer.current = null;
		}
		if (openTimer.current || openRef.current) {
			return;
		}
		openTimer.current = setTimeout(() => {
			openTimer.current = null;
			if (!calendarOverflowShouldOpen({
				suppressFocus: suppressFocusOpenRef.current,
				alreadyOpen: openRef.current,
				pointerInside: isPointerInside(),
				focusInside: isFocusInside()
			})) {
				return;
			}
			setOpenSafe(true);
		}, CALENDAR_OVERFLOW_OPEN_MS);
	};

	const scheduleClose = () => {
		if (pinnedRef.current) {
			return;
		}
		if (closeTimer.current) {
			clearTimeout(closeTimer.current);
		}
		closeTimer.current = setTimeout(() => {
			closeTimer.current = null;
			if (!calendarOverflowShouldClose({
				pinned: pinnedRef.current,
				pointerInside: isPointerInside(CALENDAR_OVERFLOW_POINTER_PAD)
			})) {
				return;
			}
			if (openTimer.current) {
				clearTimeout(openTimer.current);
				openTimer.current = null;
			}
			if (openRef.current) {
				setOpenSafe(false);
			}
		}, CALENDAR_OVERFLOW_CLOSE_MS);
	};

	useEffect(() => {
		const node = triggerRef.current;
		if (!node) {
			return undefined;
		}
		const onEnter = event => {
			notePointer(event);
			scheduleOpen();
		};
		const onLeave = event => {
			if (event.relatedTarget && containsTarget(event.relatedTarget)) {
				return;
			}
			notePointer(event);
			scheduleClose();
		};
		const onMove = event => {
			notePointer(event);
		};
		const onBlur = event => {
			if (event.relatedTarget && containsTarget(event.relatedTarget)) {
				return;
			}
			if (pinnedRef.current || isPointerInside(CALENDAR_OVERFLOW_POINTER_PAD)) {
				return;
			}
			scheduleClose();
		};
		node.addEventListener('mouseenter', onEnter);
		node.addEventListener('mouseleave', onLeave);
		node.addEventListener('mousemove', onMove);
		node.addEventListener('focus', onEnter);
		node.addEventListener('blur', onBlur);
		return () => {
			node.removeEventListener('mouseenter', onEnter);
			node.removeEventListener('mouseleave', onLeave);
			node.removeEventListener('mousemove', onMove);
			node.removeEventListener('focus', onEnter);
			node.removeEventListener('blur', onBlur);
			clearTimers();
		};
	}, []);

	useEffect(() => {
		const onMove = event => {
			notePointer(event);
			if (pinnedRef.current || !openRef.current) {
				return;
			}
			if (isPointerInside(CALENDAR_OVERFLOW_POINTER_PAD)) {
				if (closeTimer.current) {
					clearTimeout(closeTimer.current);
					closeTimer.current = null;
				}
				return;
			}
			if (!closeTimer.current) {
				scheduleClose();
			}
		};
		document.addEventListener('mousemove', onMove);
		return () => {
			document.removeEventListener('mousemove', onMove);
		};
	}, []);

	useEffect(() => {
		if (!open) {
			return undefined;
		}
		const onKeyDown = event => {
			if (event.key !== 'Escape') {
				return;
			}
			event.preventDefault();
			event.stopPropagation();
			closeAndFocusTrigger();
		};
		const onPointerDown = event => {
			if (containsTarget(event.target)) {
				return;
			}
			closeNow();
		};
		document.addEventListener('keydown', onKeyDown);
		document.addEventListener('mousedown', onPointerDown);
		return () => {
			document.removeEventListener('keydown', onKeyDown);
			document.removeEventListener('mousedown', onPointerDown);
		};
	}, [open]);

	useLayoutEffect(() => {
		if (!open || !focusListRef.current) {
			return undefined;
		}
		const focusLink = () => {
			const link = popoverRef.current && popoverRef.current.querySelector('a.calendarChip');
			if (!link) {
				return false;
			}
			focusListRef.current = false;
			link.focus();
			return true;
		};
		if (focusLink()) {
			return undefined;
		}
		const frame = window.requestAnimationFrame(focusLink);
		return () => window.cancelAnimationFrame(frame);
	}, [open]);

	const onTriggerKeyDown = event => {
		if (event.key === 'Escape') {
			event.preventDefault();
			closeAndFocusTrigger();
			return;
		}
		if (event.key !== 'Enter' && event.key !== ' ') {
			return;
		}
		event.preventDefault();
		clearTimers();
		if (openRef.current) {
			const link = popoverRef.current && popoverRef.current.querySelector('a.calendarChip');
			if (link) {
				link.focus();
			}
			return;
		}
		focusListRef.current = true;
		setOpenSafe(true);
	};

	const onTriggerClick = event => {
		if (event.detail === 0) {
			return;
		}
		clearTimers();
		if (openRef.current && pinnedRef.current) {
			closeNow();
			return;
		}
		pinnedRef.current = true;
		setOpenSafe(true);
	};

	const onPopoverKeyDown = event => {
		if (event.key === 'Escape') {
			event.preventDefault();
			closeAndFocusTrigger();
			return;
		}
		if (event.key !== 'Tab') {
			return;
		}
		const root = popoverRef.current;
		if (!root) {
			return;
		}
		const links = root.querySelectorAll('a.calendarChip');
		if (!links.length) {
			return;
		}
		const first = links[0];
		const last = links[links.length - 1];
		if (event.shiftKey && document.activeElement === first) {
			event.preventDefault();
			if (triggerRef.current) {
				triggerRef.current.focus();
			}
			return;
		}
		if (!event.shiftKey && document.activeElement === last) {
			event.preventDefault();
			closeNow();
			const trigger = triggerRef.current;
			if (!trigger) {
				return;
			}
			const focusable = document.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])');
			const list = Array.prototype.filter.call(focusable, node => node.offsetParent !== null || node === trigger);
			const index = list.indexOf(trigger);
			const next = index >= 0 ? list[index + 1] : null;
			if (next) {
				next.focus();
			} else {
				trigger.focus();
			}
		}
	};

	const onPopoverBlur = event => {
		if (event.relatedTarget && containsTarget(event.relatedTarget)) {
			return;
		}
		if (pinnedRef.current || isPointerInside()) {
			return;
		}
		scheduleClose();
	};

	const popoverId = `calendar-overflow-${dayKey}`;

	return (
		<>
			<button
				type="button"
				ref={triggerRef}
				className="calendarOverflow"
				aria-expanded={open}
				aria-haspopup="dialog"
				aria-controls={popoverId}
				onKeyDown={onTriggerKeyDown}
				onClick={onTriggerClick}>
				+{count} more
			</button>
			<Overlay
				target={triggerRef.current}
				show={open}
				placement="bottom-start"
				flip
				offset={[0, 2]}
				containerPadding={8}
				transition={false}
				popperConfig={POPPER_CONFIG}>
				{overlayProps => (
					<Popover
						{...overlayProps}
						id={popoverId}
						role="dialog"
						aria-label={`${count} more activities`}
						className={classnames('calendarOverflowPopover', overlayProps.className)}
						ref={node => {
							popoverRef.current = node;
							const popperRef = overlayProps.ref;
							if (typeof popperRef === 'function') {
								popperRef(node);
							} else if (popperRef) {
								popperRef.current = node;
							}
						}}
						onMouseEnter={() => {
							clearTimers();
						}}
						onMouseLeave={scheduleClose}
						onBlur={onPopoverBlur}
						onKeyDown={onPopoverKeyDown}>
						<Popover.Body>
							<ul className="calendarOverflowList">
								{activities.map(activity => (
									<li key={activity.activityId}>
										<CalendarChip
											activity={activity}
											dayKey={dayKey}
											datasetId={datasetId}
											colorBy={colorBy}
											codeTypeId={codeTypeId}
											datasetInfo={datasetInfo}
											onActivate={closeNow} />
									</li>
								))}
							</ul>
						</Popover.Body>
					</Popover>
				)}
			</Overlay>
		</>
	);
};

CalendarOverflow.propTypes = {
	dayKey: PropTypes.string.isRequired,
	activities: PropTypes.array.isRequired,
	datasetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	colorBy: PropTypes.string,
	codeTypeId: PropTypes.string,
	datasetInfo: PropTypes.object
};

export default CalendarOverflow;
